// client/src/utilities/routerLogicBuilder.js
// Compiles a structured list of state-based conditions into the Python
// ternary-chain expression expected by AG_GRAPH_COND_EDGE's routerLogic
// field, e.g.: 'pass' if state.get('guard_score', 0) >= 0.7 else 'fail'

export const OPERATORS_BY_TYPE = {
  number: [
    { value: '>',  label: '> (greater than)' },
    { value: '>=', label: '≥ (greater or equal)' },
    { value: '<',  label: '< (less than)' },
    { value: '<=', label: '≤ (less or equal)' },
    { value: '==', label: '= (equal to)' },
    { value: '!=', label: '≠ (not equal to)' },
  ],
  string: [
    { value: '==',           label: '= (equal to)' },
    { value: '!=',           label: '≠ (not equal to)' },
    { value: 'contains',     label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'is_empty',     label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  boolean: [
    { value: 'is_true',  label: 'is true' },
    { value: 'is_false', label: 'is false' },
  ],
  list: [
    { value: 'contains',     label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'is_empty',     label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
};

export const VALUELESS_OPERATORS = ['is_empty', 'is_not_empty', 'is_true', 'is_false'];

function defaultForType(type) {
  if (type === 'number')  return '0';
  if (type === 'boolean') return 'False';
  if (type === 'list')    return '[]';
  return "''";
}

function pyLiteral(value, type) {
  if (type === 'number') return String(Number(value) || 0);
  return `'${String(value).replace(/'/g, "\\'")}'`;
}

export function compileConditionExpr(cond, fieldType) {
  const accessor = `state.get('${cond.field}', ${defaultForType(fieldType)})`;
  const val = pyLiteral(cond.value, fieldType);

  switch (cond.operator) {
    case '>': case '>=': case '<': case '<=':
      return `${accessor} ${cond.operator} ${val}`;
    case '==':
      return `${accessor} == ${val}`;
    case '!=':
      return `${accessor} != ${val}`;
    case 'contains':
      return `${val} in ${accessor}`;
    case 'not_contains':
      return `${val} not in ${accessor}`;
    case 'is_empty':
      return `len(${accessor}) == 0`;
    case 'is_not_empty':
      return `len(${accessor}) > 0`;
    case 'is_true':
      return `${accessor} is True`;
    case 'is_false':
      return `${accessor} is False`;
    default:
      return accessor;
  }
}

// conditions evaluated in order — first match wins, like if/elif/else.
// elseBranch is the final fallback if none match.
export function compileRouterLogic(conditions, elseBranch, fieldTypeMap) {
  const validConditions = (conditions || []).filter(c => c.field && c.operator && c.branch);
  const fallback = elseBranch ? `'${elseBranch}'` : "'END'";
  if (validConditions.length === 0) return fallback;

  return validConditions.reduceRight((acc, cond) => {
    const expr = compileConditionExpr(cond, fieldTypeMap[cond.field] || 'string');
    return `'${cond.branch}' if ${expr} else ${acc}`;
  }, fallback);
}